import AuthenticationHelper from 'amazon-cognito-identity-js/lib/AuthenticationHelper';
import UserAgent from 'amazon-cognito-identity-js/lib/UserAgent';
import { Buffer } from 'buffer';

const safeCall = async (fn) => {
  try {
    return await fn();
  } catch (_) {
    return null;
  }
};

const getDeviceName = () => {
  try {
    return UserAgent?.prototype?.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : 'web');
  } catch (_) {
    return typeof navigator !== 'undefined' ? navigator.userAgent : 'web';
  }
};

const tryLoadCachedDevice = (user) => {
  try {
    if (typeof user?.getCachedDeviceKeyAndPassword === 'function') {
      user.getCachedDeviceKeyAndPassword();
    }
  } catch (_) {
    // ignore
  }
};

export const isCognitoDeviceDoesNotExistError = (err) => {
  const code = err?.code || err?.name || err?.__type;
  const message = String(err?.message || '').toLowerCase();

  if (code === 'ResourceNotFoundException' && message.includes('device')) return true;
  if (message.includes('device') && (message.includes('does not exist') || message.includes("doesn't exist") || message.includes('not found'))) {
    return true;
  }

  return false;
};

export const getCachedDeviceKey = (user) => {
  tryLoadCachedDevice(user);
  return user?.deviceKey || null;
};

const loadDeviceContextFromStorage = ({ clientId, username }) => {
  try {
    if (!clientId || !username || typeof localStorage === 'undefined') return null;

    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
    const deviceKey = localStorage.getItem(`${keyPrefix}.deviceKey`);
    const randomPassword = localStorage.getItem(`${keyPrefix}.randomPasswordKey`);
    const deviceGroupKey = localStorage.getItem(`${keyPrefix}.deviceGroupKey`);

    if (!deviceKey || !randomPassword || !deviceGroupKey) return null;

    return { deviceKey, randomPassword, deviceGroupKey };
  } catch (_) {
    return null;
  }
};

export const clearDeviceContextFromStorage = ({ clientId, usernames }) => {
  try {
    if (!clientId || typeof localStorage === 'undefined') return;

    const candidates = Array.isArray(usernames) ? usernames.filter(Boolean) : [];
    candidates.forEach((username) => {
      const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
      localStorage.removeItem(`${keyPrefix}.deviceKey`);
      localStorage.removeItem(`${keyPrefix}.randomPasswordKey`);
      localStorage.removeItem(`${keyPrefix}.deviceGroupKey`);
    });
  } catch (_) {
    // ignore
  }
};

export const hydrateUserDeviceContext = ({ user, clientId, usernames }) => {
  if (!user) return null;

  // First try whatever is already on the user instance.
  tryLoadCachedDevice(user);
  if (user?.deviceKey && user?.randomPassword && user?.deviceGroupKey) {
    return {
      deviceKey: user.deviceKey,
      randomPassword: user.randomPassword,
      deviceGroupKey: user.deviceGroupKey,
    };
  }

  const candidates = Array.isArray(usernames) ? usernames.filter(Boolean) : [];
  for (const username of candidates) {
    const ctx = loadDeviceContextFromStorage({ clientId, username });
    if (ctx) {
      user.deviceKey = ctx.deviceKey;
      user.randomPassword = ctx.randomPassword;
      user.deviceGroupKey = ctx.deviceGroupKey;
      return ctx;
    }
  }

  return null;
};

export const completeDeviceSrpAuth = ({ user, session }) => {
  return new Promise((resolve, reject) => {
    try {
      if (!user || !session) {
        reject(new Error('Missing device SRP session'));
        return;
      }
      if (typeof user.getDeviceResponse !== 'function') {
        reject(new Error('Device SRP not supported by Cognito SDK'));
        return;
      }

      user.Session = session;
      user.getDeviceResponse({
        onSuccess: (cognitoUserSession) => {
          if (!cognitoUserSession || typeof cognitoUserSession.getIdToken !== 'function') {
            // SDK returned an unexpected value (e.g. NEW_PASSWORD_REQUIRED was not routed
            // to newPasswordRequired callback). Treat as a recoverable failure so the
            // caller can retry InitiateAuth without a device key.
            reject(Object.assign(new Error('DeviceSRPUnexpectedResponse'), {
              code: 'DeviceSRPUnexpectedResponse',
            }));
            return;
          }
          resolve(cognitoUserSession);
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: (_userAttributes, _requiredAttributes) => {
          // Cognito requires a password change even after device SRP succeeded.
          // Signal the caller to clear device context and retry without the device key
          // so a clean NEW_PASSWORD_REQUIRED challenge is returned by InitiateAuth.
          reject(Object.assign(new Error('NewPasswordRequiredAfterDeviceSRP'), {
            code: 'NewPasswordRequiredAfterDeviceSRP',
          }));
        },
      });
    } catch (e) {
      reject(e);
    }
  });
};

const confirmNewDevice = ({ user, authenticationResult }) => {
  const newDeviceMetadata = authenticationResult?.NewDeviceMetadata;
  const deviceKey = newDeviceMetadata?.DeviceKey;
  const deviceGroupKey = newDeviceMetadata?.DeviceGroupKey;

  if (!deviceKey || !deviceGroupKey) return Promise.resolve(null);

  return new Promise((resolve) => {
    try {
      const poolName = user?.pool?.getUserPoolName?.() || user?.pool?.getUserPoolId?.() || '';
      const authenticationHelper = new AuthenticationHelper(poolName);

      authenticationHelper.generateHashDevice(deviceGroupKey, deviceKey, (errGenHash) => {
        if (errGenHash) {
          resolve(null);
          return;
        }

        const deviceSecretVerifierConfig = {
          Salt: Buffer.from(authenticationHelper.getSaltDevices(), 'hex').toString('base64'),
          PasswordVerifier: Buffer.from(authenticationHelper.getVerifierDevices(), 'hex').toString('base64'),
        };

        user.verifierDevices = deviceSecretVerifierConfig.PasswordVerifier;
        user.deviceGroupKey = deviceGroupKey;
        user.randomPassword = authenticationHelper.getRandomPassword();

        const accessToken = user?.signInUserSession?.getAccessToken?.()?.getJwtToken?.();
        if (!accessToken) {
          resolve(null);
          return;
        }

        user.client.request('ConfirmDevice', {
          DeviceKey: deviceKey,
          AccessToken: accessToken,
          DeviceSecretVerifierConfig: deviceSecretVerifierConfig,
          DeviceName: getDeviceName(),
        }, (errConfirm, dataConfirm) => {
          if (errConfirm) {
            resolve(null);
            return;
          }

          try {
            user.deviceKey = deviceKey;
            if (typeof user.cacheDeviceKeyAndPassword === 'function') {
              user.cacheDeviceKeyAndPassword();
            }
          } catch (_) {
            // ignore
          }

          resolve(dataConfirm);
        });
      });
    } catch (_) {
      resolve(null);
    }
  });
};

const rememberDeviceBestEffort = async (user) => {
  if (typeof user?.setDeviceStatusRemembered !== 'function') return;

  await safeCall(() => new Promise((resolve) => {
    try {
      user.setDeviceStatusRemembered({
        onSuccess: () => resolve(true),
        onFailure: () => resolve(false),
      });
    } catch (_) {
      resolve(false);
    }
  }));
};

/**
 * Ensures Cognito has a device record + marks it "remembered".
 *
 * - If `authenticationResult.NewDeviceMetadata` exists: ConfirmDevice + cache device key/password, then remember.
 * - If not: no-op.
 *
 * Never throws and should not block login UX.
 */
export const confirmAndRememberDevice = async ({ user, authenticationResult }) => {
  if (!user || !authenticationResult) return;

  // Avoid extra network calls on normal sign-in: only do device operations
  // when Cognito tells us this is a new device.
  if (!authenticationResult?.NewDeviceMetadata?.DeviceKey) {
    return;
  }

  tryLoadCachedDevice(user);

  const confirmResult = await confirmNewDevice({ user, authenticationResult });

  // Even in "Always remember" mode, calling this here is safe and ensures
  // the device status is remembered when Cognito requires it.
  if (confirmResult) {
    await rememberDeviceBestEffort(user);
  }
};
